"use client"

import { Suspense, useCallback, useEffect, useRef, useState } from "react"
import { useQueryState } from "nuqs"
import { useRouter } from "next/navigation"
import { Mic, MessageSquare, Play, LogIn, ArrowRight, Loader2, ChevronUp, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useControlBar } from "@/components/control-bar"
import { useSidebar } from "@/components/ui/sidebar"
import { replaceQuestionVariables } from "@/lib/question-variables"

export default function AppPage() {
  return (
    <Suspense>
      <AppPageInner />
    </Suspense>
  )
}

function SignInBanner() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)

  return (
    <div className="px-6 pt-4">
      <div className="flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
        <LogIn className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <p className="flex-1 text-sm text-muted-foreground">
          Sign in to practice for free.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          disabled={loading}
          style={{ minWidth: loading ? undefined : "fit-content" }}
          onClick={() => {
            setLoading(true)
            router.push("/login")
          }}
        >
          {loading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              Sign In
              <ArrowRight className="ml-1.5 h-3 w-3" />
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

function AppPageInner() {
  const [selectedId, setSelectedId] = useQueryState("q", { shallow: true })
  const router = useRouter()
  const { registerCta, accessTier, user, questions } = useControlBar()
  const question = questions.find((q) => q.id === selectedId) ?? questions[0]
  const { setOpen } = useSidebar()
  const [permissionsGranted, setPermissionsGranted] = useState(true)

  // Default to first question in URL if none selected
  useEffect(() => {
    if (!selectedId && questions.length > 0) setSelectedId(questions[0].id)
  }, [selectedId, setSelectedId])

  // Check camera/mic permissions (query only, no prompt)
  useEffect(() => {
    async function check() {
      try {
        const [mic, cam] = await Promise.all([
          navigator.permissions.query({ name: "microphone" }),
          navigator.permissions.query({ name: "camera" }),
        ])
        setPermissionsGranted(mic.state === "granted" && cam.state === "granted")
        const update = () => check()
        mic.addEventListener("change", update)
        cam.addEventListener("change", update)
        return () => {
          mic.removeEventListener("change", update)
          cam.removeEventListener("change", update)
        }
      } catch {
        // Permissions API not supported — assume not granted
        setPermissionsGranted(false)
      }
    }
    check()
  }, [])

  // Expand sidebar once when entering browse mode
  const didMount = useRef(false)
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      setOpen(true)
    }
  })

  // Swipe up/down to navigate questions on mobile
  const touchStartY = useRef(null)
  const currentIdx = questions.indexOf(question)
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [slideDir, setSlideDir] = useState(null) // "up" | "down" | null

  const goToQuestion = useCallback((idx, direction) => {
    if (idx < 0 || idx >= questions.length) {
      setSwipeOffset(0)
      return
    }
    setSlideDir(direction)
    // Wait for exit animation, then switch question
    setTimeout(() => {
      setSelectedId(questions[idx].id)
      setSlideDir(null)
      setSwipeOffset(0)
    }, 150)
  }, [questions, setSelectedId])

  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY
  }, [])

  const handleTouchMove = useCallback((e) => {
    if (touchStartY.current === null) return
    const delta = touchStartY.current - e.touches[0].clientY
    // Dampen the drag so it feels weighted
    setSwipeOffset(delta * 0.4)
  }, [])

  const handleTouchEnd = useCallback((e) => {
    if (touchStartY.current === null) return
    const delta = touchStartY.current - e.changedTouches[0].clientY
    touchStartY.current = null
    if (Math.abs(delta) < 50) {
      setSwipeOffset(0)
      return
    }
    if (delta > 0) goToQuestion(currentIdx + 1, "up")
    else goToQuestion(currentIdx - 1, "down")
  }, [currentIdx, goToQuestion])

  useEffect(() => {
    if (!user) {
      registerCta({
        label: "Start Station",
        icon: Play,
        onClick: () => router.push("/login"),
      })
    } else {
      registerCta({
        label: "Start Station",
        icon: Play,
        disabled: !question,
        onClick: () => router.push(`/app/${selectedId}`),
      })
    }
    return () => registerCta(null)
  }, [question, selectedId, registerCta, router, user])

  return (
    <div className="flex flex-1 flex-col">
      {!user && (
        <SignInBanner />
      )}

      <div
        className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-4 py-6 sm:p-8"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {question ? (
          <>
            {/* Chevron up */}
            <button
              onClick={() => goToQuestion(currentIdx - 1, "down")}
              disabled={currentIdx <= 0}
              className="relative z-10 mb-1 rounded-full p-1 text-muted-foreground/40 transition-colors hover:bg-accent disabled:invisible sm:hidden"
            >
              <ChevronUp className="h-5 w-5" />
            </button>

            {/* Question text with fade edges on mobile */}
            <div className="relative flex flex-1 items-center overflow-hidden">
              {/* Top fade */}
              <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-background to-transparent sm:hidden" />
              {/* Bottom fade */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-background to-transparent sm:hidden" />

              <div
                className="flex flex-1 items-center px-2"
                style={{
                  transform: slideDir === "up"
                    ? "translateY(-100%)"
                    : slideDir === "down"
                      ? "translateY(100%)"
                      : `translateY(${-swipeOffset}px)`,
                  opacity: slideDir ? 0 : 1 - Math.min(Math.abs(swipeOffset) / 200, 0.4),
                  transition: slideDir ? "transform 150ms ease-out, opacity 150ms ease-out" : "none",
                }}
              >
                <p className="max-w-2xl text-center text-lg leading-relaxed sm:text-xl sm:px-8">
                  {replaceQuestionVariables(question.question)}
                </p>
              </div>
            </div>

            {/* Chevron down */}
            <button
              onClick={() => goToQuestion(currentIdx + 1, "up")}
              disabled={currentIdx >= questions.length - 1}
              className="relative z-10 mt-1 rounded-full p-1 text-muted-foreground/40 transition-colors hover:bg-accent disabled:invisible sm:hidden"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          </>
        ) : (
          <div className="text-center">
            <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <h3 className="mt-4 text-lg font-medium">Select a question</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose a question from the sidebar to start practicing.
            </p>
          </div>
        )}
      </div>
      {user && !permissionsGranted && (
        <div className="px-6 pb-4">
          <div className="mx-auto flex w-fit items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-2.5 text-sm text-amber-600 dark:text-amber-400">
            <Mic className="h-3.5 w-3.5 shrink-0" />
            Allow mic and camera access on the next page.
          </div>
        </div>
      )}
    </div>
  )
}
